import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plane, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateInvite, activateAccount } from "@/lib/api";

const ActivateShell = ({ children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <div className="flex justify-between items-center p-4">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 font-bold">
          <Plane className="h-5 w-5 text-emerald-400" /> VENKA WORLD EXPRESS
        </button>
        <LanguageSelector />
      </div>
      <div className="flex-1 grid place-items-center px-4">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
};

export const ActivatePage = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalidReason, setInvalidReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalidReason("invalid");
      setChecking(false);
      return;
    }
    validateInvite(token)
      .then((res) => {
        if (res.valid) {
          setInvite(res);
        } else {
          setInvalidReason(res.reason === "expired" ? "expired" : res.reason === "used" ? "used" : "invalid");
        }
      })
      .catch(() => setInvalidReason("invalid"))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(t("activate.minLen"));
      return;
    }
    if (password !== confirm) {
      setError(t("activate.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await activateAccount(token, password);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || t("activate.invalid"));
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <ActivateShell>
        <div className="text-center text-slate-400 flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" />
          {t("activate.validating")}
        </div>
      </ActivateShell>
    );
  }

  if (invalidReason) {
    return (
      <ActivateShell>
        <div className="text-center" data-testid="activate-invalid">
          <div className="h-14 w-14 rounded-full bg-red-500/15 grid place-items-center mx-auto mb-4">
            <XCircle className="h-7 w-7 text-red-400" />
          </div>
          <p className="text-slate-200">{t(`activate.${invalidReason}`)}</p>
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="mt-5 border-slate-700 bg-slate-900 hover:bg-slate-800"
            data-testid="activate-home"
          >
            {t("activate.goHome")}
          </Button>
        </div>
      </ActivateShell>
    );
  }

  if (done) {
    return (
      <ActivateShell>
        <div className="text-center" data-testid="activate-done">
          <div className="h-14 w-14 rounded-full bg-emerald-400/15 grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="h-7 w-7 text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold">{t("activate.success")}</h1>
          <p className="text-slate-300 mt-2 text-sm">{t("activate.successMsg")}</p>
          <Button
            onClick={() => navigate("/account/login")}
            className="mt-5 w-full rounded-full bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold"
            data-testid="activate-login"
          >
            {t("portal.login.submit")}
          </Button>
          <button onClick={() => navigate("/")} className="mt-3 text-xs text-slate-400 hover:text-white block mx-auto">
            {t("activate.goHome")}
          </button>
        </div>
      </ActivateShell>
    );
  }

  return (
    <ActivateShell>
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold">{t("activate.title")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("activate.subtitle")}</p>
      </div>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-6"
        data-testid="activate-form"
      >
        <div className="space-y-1.5">
          <Label>{t("activate.email")}</Label>
          <Input
            readOnly
            value={invite?.email || ""}
            className="bg-slate-950 border-slate-700 text-slate-400"
            data-testid="activate-email"
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("activate.password")}</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-slate-950 border-slate-700"
            data-testid="activate-password"
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("activate.confirm")}</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="bg-slate-950 border-slate-700"
            data-testid="activate-confirm"
          />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="activate-error">{error}</p>}
        <Button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold"
          data-testid="activate-submit"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("activate.submitting")}
            </span>
          ) : (
            t("activate.submit")
          )}
        </Button>
      </form>
    </ActivateShell>
  );
};

export default ActivatePage;
